import { UploadSimpleIcon } from "@phosphor-icons/react/dist/csr/UploadSimple";
import { XCircleIcon } from "@phosphor-icons/react/dist/csr/XCircle";
import { Alert, AlertDescription } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { getErrorMessage } from "@/core/api/errors";
import { formatMoneyCents } from "@/core/utils/money";
import { useStAdjustmentLines } from "./query";
import { TechnicianAdjustmentLine } from "./technician-adjustment-line";
import { TechnicianSidebarHeader } from "./technician-sidebar-header";
import type { StAdjustmentTechnicianGroup } from "./types";
import { isPushableGroup, proposalKey } from "./utils";

interface TechnicianLinesSidebarProps {
  businessId: string | null;
  runId: string | null;
  // The rollup row this drilled down from. Null closes the panel — the shell
  // stays mounted, so the contents simply disappear.
  group: StAdjustmentTechnicianGroup | null;
  busy: boolean;
  onClose: () => void;
  onPushTechnician: (group: StAdjustmentTechnicianGroup) => void;
}

// The lines behind one rollup row. Fetched filtered by employee_id rather than
// sliced client-side: the run's lines are paged 50 at a time out of potentially
// thousands, so a technician's rows are mostly not in any page we hold.
export const TechnicianLinesSidebar = ({
  businessId,
  runId,
  group,
  busy,
  onClose,
  onPushTechnician,
}: TechnicianLinesSidebarProps) => {
  const employeeId = group?.employee_id ?? null;
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useStAdjustmentLines(businessId, runId, !!employeeId, employeeId);

  if (!group) return null;

  const pages = data?.pages ?? [];
  const lines = pages.flatMap((page) => page.items);
  const totalCount = pages[0]?.total_count ?? 0;
  const pushable = isPushableGroup(group);

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      <TechnicianSidebarHeader group={group} onClose={onClose} />

      <Stack
        align="start"
        gap={4}
        className="min-h-0 w-full flex-1 overflow-y-auto p-5"
      >
        {isLoading && (
          <Spinner layout="centered" className="h-32" tone="primary" />
        )}

        {error && !isLoading && (
          <Alert color="danger">
            <XCircleIcon />
            <AlertDescription>
              <Text>{getErrorMessage(error)}</Text>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading &&
          lines.map((line) => (
            <TechnicianAdjustmentLine
              key={proposalKey(line)}
              jobNumber={line.job_number}
              amountCents={line.amount_cents}
              category={line.category}
              memo={line.memo}
              alreadyPushed={line.already_pushed}
            />
          ))}

        {hasNextPage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchNextPage()}
            // fetchNextPage would cancel an in-flight refetch and leave stale
            // rows on screen.
            disabled={isFetchingNextPage || isFetching}
            aria-busy={isFetchingNextPage || undefined}
          >
            {isFetchingNextPage && (
              <Spinner data-icon="inline-start" aria-hidden />
            )}
            Load more
          </Button>
        )}

        {!isLoading && !error && (
          <Text variant="caption" tone="muted">
            Showing {lines.length} of {totalCount}
          </Text>
        )}
      </Stack>

      {pushable && (
        <Stack
          align="start"
          gap={4}
          className="w-full shrink-0 border-t px-5 py-4"
        >
          <Button
            className="w-full"
            disabled={busy}
            onClick={() => onPushTechnician(group)}
            aria-busy={busy || undefined}
          >
            {busy ? (
              <Spinner data-icon="inline-start" aria-hidden />
            ) : (
              <UploadSimpleIcon data-icon="inline-start" className="h-4 w-4" />
            )}
            Push {group.pending} ·{" "}
            {formatMoneyCents(group.pending_amount_cents) ?? "$0"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
